// Intégrateur de mots-clés SEO avancés - Mise à jour automatique du SEO_CONFIG
import { ADVANCED_GAME_KEYWORDS, getAllKeywordsForGame, getKeywordsByCategory } from '@/config/advancedGameKeywords';
import { SEO_CONFIG } from '@/config/seo';

// Mapping des noms de jeux entre la base de données et les clés SEO
const GAME_NAME_MAPPING: Record<string, keyof typeof ADVANCED_GAME_KEYWORDS> = {
  'Minecraft': 'minecraft',
  'Dofus Retro': 'dofus-retro',
  'Dofus 2.X': 'dofus-2x',
  'World of Warcraft': 'world-of-warcraft',
  'Counter-Strike 2 (CS2)': 'counter-strike-2',
  'Counter-Strike: Global Offensive (CS:GO)': 'counter-strike-global-offensive',
  'Counter-Strike: Source (CSS)': 'counter-strike-source',
  'GTA V (FiveM)': 'gta-v-fivem',
  'Rust': 'rust',
  'DayZ': 'dayz',
  "Garry's Mod": 'garrys-mod',
  'ARK Survival ': 'ark-survival',
  'Lineage 2': 'lineage-2',
  'Metin 2': 'metin-2',
  'Aion': 'aion',
  'Mu Online': 'mu-online',
  'Red Dead Redemption 2': 'red-dead-redemption-2',
  'Roblox': 'roblox'
  // Les jeux non listés ici utilisent automatiquement le fallback générique
  // (templates basés sur le nom du jeu, sans keyword stuffing inadapté)
};

// Génère les mots-clés complets pour un jeu donné
export const generateEnhancedKeywords = (gameName: string, language: 'fr' | 'en' | 'es' = 'fr'): string => {
  const gameKey = GAME_NAME_MAPPING[gameName];
  if (!gameKey || !ADVANCED_GAME_KEYWORDS[gameKey]) {
    // Fallback générique adapté à n'importe quel jeu (anti keyword-stuffing)
    const templates = {
      fr: `serveurs ${gameName}, serveur privé ${gameName}, top serveur ${gameName}, classement ${gameName}, vote serveur ${gameName}, meilleurs serveurs ${gameName}`,
      en: `${gameName} servers, ${gameName} server list, ${gameName} private server, best ${gameName} servers, vote ${gameName} server, ${gameName} server ranking`,
      es: `servidores ${gameName}, lista servidores ${gameName}, servidor privado ${gameName}, mejores servidores ${gameName}, votar servidor ${gameName}, ranking ${gameName}`
    };
    return templates[language];
  }

  const allKeywords = getAllKeywordsForGame(gameKey, language);
  const primaryKeywords = getKeywordsByCategory(gameKey, 'primary', language);
  
  // Combine mots-clés primaires + sélection des meilleurs mots-clés secondaires
  return [...primaryKeywords, ...allKeywords.slice(0, 20)].join(', ');
};

// Alias d'affichage pour intégrer les acronymes/abréviations populaires
// (booste la recherche sur ex: "wow private server" en plus de "world of warcraft")
const GAME_DISPLAY_ALIAS: Record<string, string> = {
  'World of Warcraft': 'World of Warcraft (WoW)',
  'Dofus 2.X': 'Dofus 2 (2.X)',
  'Counter-Strike 2 (CS2)': 'Counter-Strike 2 (CS2)',
  'Counter-Strike: Global Offensive (CS:GO)': 'Counter-Strike: Global Offensive (CS:GO)',
  'Counter-Strike: Source (CSS)': 'Counter-Strike: Source (CSS)',
  'GTA V (FiveM)': 'GTA V FiveM',
};

const getDisplayName = (gameName: string): string =>
  GAME_DISPLAY_ALIAS[gameName] || gameName;

// Génère un titre SEO optimisé pour un jeu (raccourci ≤ ~60 car. pour éviter la troncature SERP)
export const generateEnhancedTitle = (gameName: string, language: 'fr' | 'en' | 'es' = 'fr'): string => {
  const year = new Date().getFullYear();
  const displayName = getDisplayName(gameName);
  const templates = {
    fr: `Top Serveurs Privés ${displayName} ${year} | Vote & Classement`,
    en: `Best ${displayName} Private Servers ${year} | Vote & Ranking`,
    es: `Mejores Servidores Privados ${displayName} ${year} | Votar`
  };
  return templates[language];
};

// Génère une description SEO optimisée pour un jeu — neutre internationalement, CTA fort
export const generateEnhancedDescription = (gameName: string, language: 'fr' | 'en' | 'es' = 'fr'): string => {
  const displayName = getDisplayName(gameName);
  const gameKey = GAME_NAME_MAPPING[gameName];
  if (!gameKey || !ADVANCED_GAME_KEYWORDS[gameKey]) {
    const templates = {
      fr: `Découvre le top des serveurs privés ${displayName} : classement live, votes & communauté active. Rejoins gratuitement le meilleur serveur ${displayName} en 2 clics !`,
      en: `Find the best ${displayName} private servers: live ranking, votes & active community. Join the top ${displayName} server for free in 2 clicks!`,
      es: `Descubre los mejores servidores privados de ${displayName}: ranking en vivo, votos y comunidad activa. ¡Únete gratis al mejor servidor ${displayName}!`
    };
    return templates[language];
  }

  const primaryKeywords = getKeywordsByCategory(gameKey, 'primary', language);
  const gameTypes = getKeywordsByCategory(gameKey, 'gameTypes', language);
  const features = getKeywordsByCategory(gameKey, 'features', language);

  const mainKeyword = primaryKeywords[0] || gameName.toLowerCase();
  const gameTypesList = gameTypes.slice(0, 2).join(', ');
  const featuresList = features.slice(0, 2).join(', ');

  const year = new Date().getFullYear();
  const templates = {
    fr: `Top ${mainKeyword}s ${year} : ${gameTypesList || 'tous modes'}. Communauté active, ${featuresList || 'serveurs vérifiés'}. Vote & rejoins le meilleur serveur ${displayName} gratuitement !`,
    en: `Top ${mainKeyword}s ${year}: ${gameTypesList || 'all modes'}. Active community, ${featuresList || 'verified servers'}. Vote & join the best ${displayName} server for free!`,
    es: `Top ${mainKeyword}s ${year}: ${gameTypesList || 'todos los modos'}. Comunidad activa, ${featuresList || 'servidores verificados'}. ¡Vota y únete al mejor servidor ${displayName} gratis!`
  };

  return templates[language];
};


// Configuration SEO étendue pour tous les jeux avec mots-clés avancés
export const EXTENDED_SEO_CONFIG = {
  ...SEO_CONFIG,
  games: {
    ...SEO_CONFIG.games,
    // Ajouter automatiquement tous les jeux avec leurs mots-clés optimisés
    ...Object.keys(GAME_NAME_MAPPING).reduce((acc, gameName) => {
      const gameKey = GAME_NAME_MAPPING[gameName];
      if (ADVANCED_GAME_KEYWORDS[gameKey]) {
        const gameSlug = gameName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
        acc[gameSlug] = {
          fr: {
            title: generateEnhancedTitle(gameName, 'fr'),
            description: generateEnhancedDescription(gameName, 'fr'),
            keywords: generateEnhancedKeywords(gameName, 'fr')
          },
          en: {
            title: generateEnhancedTitle(gameName, 'en'),
            description: generateEnhancedDescription(gameName, 'en'),
            keywords: generateEnhancedKeywords(gameName, 'en')
          },
          es: {
            title: generateEnhancedTitle(gameName, 'es'),
            description: generateEnhancedDescription(gameName, 'es'),
            keywords: generateEnhancedKeywords(gameName, 'es')
          }
        };
      }
      return acc;
    }, {} as any)
  }
};

// Fonction pour obtenir les mots-clés par popularité/priorité
export const getKeywordsByPriority = (gameName: string, language: 'fr' | 'en' | 'es' = 'fr') => {
  const gameKey = GAME_NAME_MAPPING[gameName];
  if (!gameKey || !ADVANCED_GAME_KEYWORDS[gameKey]) return [];

  return {
    high: getKeywordsByCategory(gameKey, 'primary', language),
    medium: [
      ...getKeywordsByCategory(gameKey, 'gameTypes', language).slice(0, 5),
      ...getKeywordsByCategory(gameKey, 'types', language).slice(0, 5)
    ],
    low: [
      ...getKeywordsByCategory(gameKey, 'rates', language).slice(0, 3),
      ...getKeywordsByCategory(gameKey, 'qualifiers', language).slice(0, 3)
    ]
  };
};

// Export des utilitaires pour l'intégration
export {
  GAME_NAME_MAPPING,
  ADVANCED_GAME_KEYWORDS
};